import { useEffect, useRef, useState } from 'react';
import { ALERT_STREAM_URL } from '../alerts.api';

/** Several changes arriving together (a sweep raising a few alerts) are one refetch. */
const REFRESH_DEBOUNCE_MS = 500;

export type AlertFeedConnection = 'connecting' | 'live' | 'reconnecting';

/**
 * Keeps the inbox current (gitaumoses4/chukua#99). The server sends only that an alert changed, never its content, so this asks
 * `onChange` to refetch the list the person is looking at: their filters, page and permissions decide what they see. `EventSource`
 * reconnects by itself after a drop; the events missed meanwhile are caught up by refetching once it is back. Where there is no
 * `EventSource` (or the stream is refused) the list works as it did before, on action and navigation.
 */
export function useAlertFeed(onChange: () => void): AlertFeedConnection {
  const [connection, setConnection] = useState<AlertFeedConnection>('connecting');
  const latestOnChange = useRef(onChange);

  useEffect(() => {
    latestOnChange.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const source = new EventSource(ALERT_STREAM_URL);
    let refresh: ReturnType<typeof setTimeout> | undefined;
    let wasDropped = false;
    const scheduleRefresh = () => {
      clearTimeout(refresh);
      refresh = setTimeout(() => latestOnChange.current(), REFRESH_DEBOUNCE_MS);
    };

    source.addEventListener('alert', scheduleRefresh);
    source.onopen = () => {
      setConnection('live');
      if (wasDropped) scheduleRefresh();
      wasDropped = false;
    };
    source.onerror = () => {
      wasDropped = true;
      setConnection('reconnecting');
    };
    return () => {
      clearTimeout(refresh);
      source.close();
    };
  }, []);

  return connection;
}
